import { createTheme } from '@mui/material/styles';

/**
 * Material UI theme configuration based on Thomson Reuters website
 * Modernized with enhanced shadows, spacing, typography, and animations
 * This theme will be applied to all Material UI components
 */

// Modern shadow system with multiple elevation levels
const shadows = [
  'none',
  '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
  '0 2px 4px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
  '0 4px 6px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)',
  '0 8px 10px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.06)',
  '0 12px 16px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.08)',
  '0 16px 24px rgba(0, 0, 0, 0.1), 0 6px 12px rgba(0, 0, 0, 0.08)',
  '0 20px 32px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.1)',
  '0 24px 40px rgba(0, 0, 0, 0.12), 0 10px 20px rgba(0, 0, 0, 0.1)',
  '0 28px 48px rgba(0, 0, 0, 0.14), 0 12px 24px rgba(0, 0, 0, 0.12)',
  '0 32px 56px rgba(0, 0, 0, 0.14), 0 14px 28px rgba(0, 0, 0, 0.12)',
  '0 36px 64px rgba(0, 0, 0, 0.16), 0 16px 32px rgba(0, 0, 0, 0.14)',
  '0 40px 72px rgba(0, 0, 0, 0.16), 0 18px 36px rgba(0, 0, 0, 0.14)',
  '0 44px 80px rgba(0, 0, 0, 0.18), 0 20px 40px rgba(0, 0, 0, 0.16)',
  '0 48px 88px rgba(0, 0, 0, 0.18), 0 22px 44px rgba(0, 0, 0, 0.16)',
  '0 52px 96px rgba(0, 0, 0, 0.2), 0 24px 48px rgba(0, 0, 0, 0.18)',
  '0 56px 104px rgba(0, 0, 0, 0.2), 0 26px 52px rgba(0, 0, 0, 0.18)',
  '0 60px 112px rgba(0, 0, 0, 0.22), 0 28px 56px rgba(0, 0, 0, 0.2)',
  '0 64px 120px rgba(0, 0, 0, 0.22), 0 30px 60px rgba(0, 0, 0, 0.2)',
  '0 68px 128px rgba(0, 0, 0, 0.24), 0 32px 64px rgba(0, 0, 0, 0.22)',
  '0 72px 136px rgba(0, 0, 0, 0.24), 0 34px 68px rgba(0, 0, 0, 0.22)',
  '0 76px 144px rgba(0, 0, 0, 0.26), 0 36px 72px rgba(0, 0, 0, 0.24)',
  '0 80px 152px rgba(0, 0, 0, 0.26), 0 38px 76px rgba(0, 0, 0, 0.24)',
  '0 84px 160px rgba(0, 0, 0, 0.28), 0 40px 80px rgba(0, 0, 0, 0.26)',
];

const theme = createTheme({
  palette: {
    primary: {
      main: '#1A2E22', // Thomson Reuters dark green/black
      light: '#2C4A3A',
      dark: '#0F1A14',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D74B00', // Thomson Reuters orange/red
      light: '#E67234',
      dark: '#B33F00',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif',
    h1: {
      fontWeight: 300,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 300,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 400,
      fontSize: '1.75rem',
      lineHeight: 1.35,
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 10,
  },
  spacing: 8, // 8px base unit for consistent spacing
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  shadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '0.9375rem',
          fontWeight: 500,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: shadows[2],
          '&:hover': {
            boxShadow: shadows[4],
            transform: 'translateY(-2px)',
          },
          '&:active': {
            boxShadow: shadows[1],
            transform: 'translateY(0)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: shadows[2],
          borderRadius: 12,
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: shadows[4],
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
        },
        title: {
          fontSize: '1.25rem',
          fontWeight: 600,
          lineHeight: 1.4,
        },
        subheader: {
          fontSize: '0.875rem',
          marginTop: '4px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '&:last-child': {
            paddingBottom: '20px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadows[3],
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: shadows[2],
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: shadows[2],
        },
        elevation2: {
          boxShadow: shadows[4],
        },
        elevation3: {
          boxShadow: shadows[6],
        },
        elevation4: {
          boxShadow: shadows[8],
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8F9FA',
          '& .MuiTableCell-root': {
            borderBottom: '2px solid rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(26, 46, 34, 0.04)',
              transform: 'scale(1.001)',
            },
            '&:nth-of-type(even)': {
              backgroundColor: 'rgba(0, 0, 0, 0.01)',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
        head: {
          fontWeight: 600,
          color: '#1A2E22',
          fontSize: '0.875rem',
          letterSpacing: '0.02em',
        },
        body: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',
          padding: '12px 20px',
          minHeight: '48px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(26, 46, 34, 0.04)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1A2E22',
              borderWidth: '1.5px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1A2E22',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.8125rem',
          height: '28px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(26, 46, 34, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(215, 75, 0, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(215, 75, 0, 0.16)',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(26, 46, 34, 0.08)',
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: shadows[8],
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: shadows[1],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        // By default MUI aligns the selected item with the input, which can
        // push the menu up and clip the topmost option(s). Always open below
        // the field instead so the first item is never truncated.
        MenuProps: {
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        },
      },
    },
  },
});

export default theme;
